import React, { useState } from 'react';
import { Box, Typography, Button, Divider, Alert } from '@mui/material';
import { BRAND } from '../branding';
import { LoginButton } from '../components/Auth/LoginButton';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { clearAuth0Cache } from '../utils/auth0-session';
import RefreshIcon from '@mui/icons-material/Refresh';

const Login: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [sessionCleared, setSessionCleared] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/order-fulfillment/operations-dashboard" replace />;
  }

  const handleClearSession = () => {
    clearAuth0Cache();
    setSessionCleared(true);
    setTimeout(() => setSessionCleared(false), 3000);
  };

  return (
    <>
      {/* Figtree + Noto Sans — healthcare-recommended font pairing */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=Noto+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <style>{`
        @keyframes loginCardEntrance {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes loginOrbDrift {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(12px, -8px); }
        }
      `}</style>

      {/* Full-viewport background — warm off-white per pharmacy palette */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(160deg, #F8FAFC 0%, #F1F5F9 40%, #EFF6F0 100%)',
          position: 'relative',
          overflow: 'hidden',
          px: 2,
          py: 4,
        }}
      >
        {/* ── Ambient Orbs ── */}

        {/* Blue orb — top right */}
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            right: '-5%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,86,210,0.07) 0%, transparent 65%)',
            filter: 'blur(60px)',
            animation: 'loginOrbDrift 22s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />

        {/* Green orb — bottom left */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '-8%',
            left: '-4%',
            width: 520,
            height: 520,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(21,128,61,0.06) 0%, transparent 65%)',
            filter: 'blur(60px)',
            animation: 'loginOrbDrift 28s ease-in-out infinite reverse',
            pointerEvents: 'none',
          }}
        />

        {/* Soft center glow for depth */}
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(ellipse, rgba(255,255,255,0.7) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />

        {/* ── Login Card ── */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            borderRadius: '14px',
            overflow: 'hidden',
            bgcolor: '#ffffff',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow:
              '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px -4px rgba(0,0,0,0.08), 0 20px 48px -8px rgba(0,0,0,0.05)',
            opacity: 0,
            animation:
              'loginCardEntrance 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Top accent bar — brand blue to green */}
          <Box
            sx={{
              height: 5,
              background: 'linear-gradient(90deg, #0056d2 0%, #15803D 100%)',
            }}
          />

          {/* Card content */}
          <Box
            sx={{
              px: { xs: 3.5, sm: 5 },
              pt: { xs: 3.5, sm: 4 },
              pb: { xs: 3, sm: 3.5 },
            }}
          >
            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img
                src={BRAND.logo}
                alt={BRAND.name}
                width={366}
                height={80}
                style={{ height: '64px', width: 'auto', objectFit: 'contain' }}
              />
            </Box>

            {/* Subtitle */}
            <Typography
              sx={{
                fontFamily: '"Noto Sans", -apple-system, sans-serif',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: '#64748B',
                textAlign: 'center',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 3.5,
              }}
            >
              Workflow Management System
            </Typography>

            {/* Login button — 44px+ touch target, focus ring for a11y */}
            <Box
              sx={{
                '& .MuiButton-root': {
                  py: 1.5,
                  minHeight: '48px',
                  fontSize: '0.95rem',
                  fontFamily: '"Figtree", -apple-system, sans-serif',
                  fontWeight: 600,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #0056d2 0%, #004ABB 100%)',
                  boxShadow:
                    '0 2px 8px rgba(0,86,210,0.25), 0 0 0 0 rgba(0,86,210,0)',
                  textTransform: 'none',
                  letterSpacing: '0.01em',
                  transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, #0062e6 0%, #0056d2 100%)',
                    boxShadow: '0 4px 16px rgba(0,86,210,0.35)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0) scale(0.99)',
                    boxShadow: '0 1px 4px rgba(0,86,210,0.2)',
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    boxShadow:
                      '0 2px 8px rgba(0,86,210,0.25), 0 0 0 3px rgba(0,86,210,0.3)',
                  },
                },
              }}
            >
              <LoginButton
                fullWidth
                variant="contained"
                forceClean={sessionCleared}
              />
            </Box>

            {/* Dev tools (dev mode only) */}
            {import.meta.env.MODE === 'development' && (
              <>
                <Divider sx={{ my: 2.5, borderColor: '#F1F5F9' }} />
                <Box>
                  <Typography
                    sx={{
                      fontFamily: '"Noto Sans", -apple-system, sans-serif',
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      color: '#CBD5E1',
                      textAlign: 'center',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      mb: 1,
                    }}
                  >
                    Development Tools
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={
                      <RefreshIcon sx={{ fontSize: '16px !important' }} />
                    }
                    onClick={handleClearSession}
                    size="small"
                    sx={{
                      fontFamily: '"Noto Sans", -apple-system, sans-serif',
                      fontSize: '0.78rem',
                      fontWeight: 500,
                      borderColor: '#E2E8F0',
                      color: '#64748B',
                      borderRadius: '8px',
                      textTransform: 'none',
                      minHeight: '40px',
                      cursor: 'pointer',
                      transition: 'all 150ms ease',
                      '&:hover': {
                        borderColor: '#CBD5E1',
                        bgcolor: '#F8FAFC',
                      },
                      '&:focus-visible': {
                        outline: 'none',
                        boxShadow: '0 0 0 3px rgba(0,86,210,0.2)',
                      },
                    }}
                  >
                    Clear Auth0 Session
                  </Button>
                  {sessionCleared && (
                    <Alert
                      severity="success"
                      sx={{ mt: 1.5, borderRadius: '8px', fontSize: '0.8rem' }}
                    >
                      Session cleared. Try logging in again.
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Login;
